import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {SponsorshipData} from "types/Sponsorship";
import SponsorshipForm from "pages/Sponsorship/Form";
import {FormikHelpers} from "formik";
import {useOperator} from "context/OperatorContext";

function MutateSponsorship() {
    const {id} = useParams()
    const {data: sponsorship, isLoading} = useQuery(
        ['sponsorships', {id}],
        () => ngage.sponsorships.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: SponsorshipData) => ngage.sponsorships.store(data))
    const update = useMutation((data: SponsorshipData) => ngage.sponsorships.update(parseInt(id as string), data))

    const {termFor} = useOperator()
    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: SponsorshipData, {setSubmitting}: FormikHelpers<SponsorshipData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('sponsorships.all')

        setSubmitting(false)
        navigate('/sponsorships')
    }

    // Handle store mutation
    async function handleStore(values: SponsorshipData) {
        try {
            await store.mutateAsync(values)
            notify("success", `${termFor('Sponsorship')} Created`, `A new ${termFor('sponsorship')} has been created`)
        } catch (error) {
            notify("error", `${termFor('Sponsorship')} Not Created`, `A new ${termFor('sponsorship')} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: SponsorshipData) {
        try {
            await update.mutateAsync(values)
            notify("success", `${termFor('Sponsorship')} Updated`, `The chosen ${termFor('sponsorship')} has been updated`)
            await invalidate(['sponsorships', {id}])
        } catch (error) {
            notify("error", `${termFor('Sponsorship')} Not Updated`, `The chosen ${termFor('sponsorship')} could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {
        'Commercial': '/',
        [termFor('Sponsorships')]: '/sponsorships',
        [`New ${termFor('Sponsorship')}`]: '/sponsorships/new'
    }
    const editBreadcrumbs = {
        'Commercial': '/',
        [termFor('Sponsorships')]: '/sponsorships',
        [`Edit ${termFor('Sponsorship')}`]: `/sponsorships/${id}/edit`
    }

    return (
        <Page title={`${id? 'Edit': 'New'} ${termFor('Sponsorship')}`}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={isLoading}>
            <SponsorshipForm sponsorship={sponsorship} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateSponsorship;